/**
 * Client side of the per-contact activity log (World/{uid}/db/{id}/activityLog).
 * Same shape as functions/helpers/contactActivityLog.js; firestore.rules
 * enforces it. Writes are best-effort and never block the edit itself.
 */
import { collection, doc, setDoc, writeBatch } from 'firebase/firestore';

export const ACTIVITY_TYPES = new Set(['outreach_enrolled', 'edited', 'status', 'email_check', 'dm_emails']);

const clip = (v, max) => {
  const s = String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

export function buildActivityEvent({ type, title, detail = '', at = Date.now(), jobId = '' } = {}) {
  if (!ACTIVITY_TYPES.has(type)) return null;
  const head = clip(title, 160);
  if (!head) return null;
  const when = new Date(typeof at === 'number' ? at : Date.parse(at));
  if (Number.isNaN(when.getTime())) return null;
  const event = { type, at: when.toISOString(), title: head, detail: clip(detail, 300) };
  if (jobId) event.jobId = clip(jobId, 120);
  return event;
}

const activityDoc = (firestore, userId, contactId) => doc(collection(firestore, `World/${userId}/db/${contactId}/activityLog`));

export function logContactActivity(firestore, userId, contactId, input) {
  if (!firestore || !userId || !contactId) return Promise.resolve();
  const event = buildActivityEvent(input);
  if (!event) return Promise.resolve();
  return setDoc(activityDoc(firestore, userId, contactId), event)
    .catch((err) => console.warn('[activity-log] append skipped:', err && err.message));
}

export function addContactActivityToBatch(batch, firestore, userId, contactId, input) {
  const event = buildActivityEvent(input);
  if (!event || !batch || !userId || !contactId) return;
  batch.set(activityDoc(firestore, userId, contactId), event);
}

/** One "Added to outreach agent" entry per contact, written in chunks. */
export async function logOutreachEnrollment(firestore, userId, contactIds, { jobId, jobName, detail = '', at = Date.now() } = {}) {
  const event = {
    type: 'outreach_enrolled',
    title: `Added to outreach agent${jobName ? `: ${jobName}` : ''}`,
    detail,
    at,
    jobId,
  };
  const ids = (contactIds || []).filter(Boolean);
  for (let i = 0; i < ids.length; i += 450) {
    const batch = writeBatch(firestore);
    ids.slice(i, i + 450).forEach((cid) => addContactActivityToBatch(batch, firestore, userId, cid, event));
    try {
      await batch.commit();
    } catch (err) {
      console.warn('[activity-log] enrollment batch skipped:', err && err.message);
    }
  }
}

const FIELD_LABELS = {
  business_name: 'Company name', name: 'Name', first_name: 'First name', last_name: 'Last name',
  lead_email: 'Email', email: 'Email', lead_phone: 'Phone', phone: 'Phone',
  lead_website: 'Website', lead_address: 'Address', lead_city: 'City', lead_country: 'Country',
  lead_contacted: 'Status', lead_notes: 'Notes', lead_decision_makers: 'Decision makers',
  lead_socials: 'Social profiles', lead_linkedin: 'LinkedIn', lead_instagram: 'Instagram',
  lead_facebook: 'Facebook', lead_industry: 'Industry', industry: 'Industry',
  lead_description: 'Description', description: 'Description', lead_value: 'Deal value',
};

const SKIP_FIELDS = /(^_|_dedup$|_norm(alized)?$|^lead_last_edit$|^updated_at$|^has_email$)/;

const humanize = (key) => FIELD_LABELS[key]
  || String(key).replace(/^lead_/, '').replace(/[_.]+/g, ' ').replace(/^./, (c) => c.toUpperCase());

const shortValue = (v) => {
  if (v == null || v === '') return '(empty)';
  if (typeof v === 'object') return '';
  return clip(v, 60);
};

const STATUS_FIELDS = new Set(['lead_contacted', 'lead_status']);

/** "Status: Closed" + "was Interested · in the email window", or null if unchanged. */
export function statusChangeEvent(from, to, via = '') {
  const next = clip(to, 80);
  const prev = clip(from, 80) || 'Not Contacted';
  if (!next || next === prev) return null;
  return {
    type: 'status',
    title: `Status: ${next}`,
    detail: [`was ${prev}`, via].filter(Boolean).join(' · '),
  };
}

/** Writes a status entry when the status really changed. */
export function logStatusChange(firestore, userId, contactId, from, to, via = '') {
  const event = statusChangeEvent(from, to, via);
  return event ? logContactActivity(firestore, userId, contactId, event) : Promise.resolve();
}

/**
 * "Edited: Phone" + "Phone: +43 1 → +43 2" for a set of changed fields.
 * Values longer than a short line (notes, JSON fields) are named, not quoted.
 * A status change alone becomes a status entry.
 */
export function describeEdit(updates, before = {}) {
  const keys = Object.keys(updates || {}).filter((k) => !SKIP_FIELDS.test(k));
  if (keys.length === 0) return null;
  if (keys.every((k) => STATUS_FIELDS.has(k))) {
    const key = keys.includes('lead_contacted') ? 'lead_contacted' : keys[0];
    return statusChangeEvent(before.lead_contacted || before.lead_status, updates[key]);
  }
  const labels = [...new Set(keys.map(humanize))];
  const quotable = keys.length === 1 && !/decision_makers|socials|notes|description/i.test(keys[0]);
  const from = quotable ? shortValue(before[keys[0]]) : '';
  const to = quotable ? shortValue(updates[keys[0]]) : '';
  return {
    type: 'edited',
    title: `Edited: ${clip(labels.join(', '), 140)}`,
    detail: quotable && (from || to) ? `${from} → ${to}` : '',
  };
}
